import { CMSApiResponse } from "framework/http/CMSApiClient";
import { RunContextApp } from "framework/RCApp";
import {
  StringRegistryItem,
  StringRegistryListMeta,
  StringTemplate,
  StringTemplateItem
} from "../types/stringRegistryTypes";
import { API_TIMEOUT } from "constants/global";
import { normalizeLocaleCode } from "../utils/localeDisplay";
import { hasGlobalLocaleData } from "../utils/globalTemplateLocales";

const STRINGS_LIST = "/string-registry/v1/strings";
const STRING_DETAIL_V1 = (id: string) => `/string-registry/v1/strings/${id}`;
const STRING_WITH_TEMPLATE = (id: string) => `/string-registry/v1/strings/${id}/with-template`;
const LOCALES = "/string-registry/v1/locales";
const ENTRIES_CRUD = "/string-registry-entries";
const ENTRY_UPDATE = (id: string) => `/string-registry-entries/${id}`;
const TEMPLATES_LIST = "/string-registry-template/v1/list";
const TEMPLATE_DETAIL = (id: string) => `/string-registry-templates/${id}`;
const TEMPLATE_VALUES = (id: string) => `/string-registry-template/v1/${id}/values`;
const TEMPLATE_VALUE_DELETE = (id: string, entryId: string) =>
  `/string-registry-template/v1/${id}/values/${entryId}`;
const TEMPLATE_CLONE = (id: string) => `/string-registry-template/v1/${id}/clone`;
const WORKFLOW_SUBMIT = (id: string) => `/string-registry-entry/v1/${id}/submit`;
const WORKFLOW_APPROVE = (id: string) => `/string-registry-entry/v1/${id}/approve`;
const WORKFLOW_REJECT = (id: string) => `/string-registry-entry/v1/${id}/reject`;
const WORKFLOW_REOPEN = (id: string) => `/string-registry-entry/v1/${id}/reopen`;

export interface ListStringsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  verificationStatus?: string;
  component?: string;
  section?: string;
  deprecated?: boolean;
  /**
   * Template documentId — the server omits entries already in it, before
   * counting, so total/pageCount describe only what's addable. Older backends
   * ignore it, so callers must still tolerate member rows in the response.
   */
  excludeTemplate?: string;
  /** When set with {@link localePresence}, filters entries by GLOBAL template overrides for this locale. */
  locale?: string;
  /** Requires `locale`. `has` = non-empty value; `missing` = no row or empty value. */
  localePresence?: "has" | "missing";
}

export interface CreateStringEntryPayload {
  stringResourceId: string;
  displayName: string;
  documentation: string;
  deprecated?: boolean;
  lookupDocumentIds: string[];
}

function entryPayload(data: CreateStringEntryPayload) {
  const connect = data.lookupDocumentIds.map((documentId) => ({ documentId }));
  return {
    stringResourceId: data.stringResourceId,
    displayName: data.displayName,
    documentation: data.documentation,
    deprecated: data.deprecated ?? false,
    ...(connect.length ? { lookups: { connect } } : {})
  };
}

/** Persist only non-empty template overrides (empty = inherit global). */
export function compactLocaleOverridesForPayload(
  overrides: { locale?: string; value?: string }[] | undefined
): { locale: string; value: string }[] {
  return (overrides || [])
    .map((o) => ({
      locale: normalizeLocaleCode(o?.locale ?? ""),
      value: String(o?.value ?? "")
    }))
    .filter((o) => o.locale && o.value.trim() !== "");
}

export const StringRegistryApis = {
  getStrings: async (
    rc: RunContextApp,
    params: ListStringsParams = {}
  ): Promise<CMSApiResponse<StringRegistryItem[]> & { meta?: StringRegistryListMeta }> => {
    const q = new URLSearchParams();
    if (params.page) q.append("page", String(params.page));
    if (params.pageSize) q.append("pageSize", String(params.pageSize));
    if (params.search) q.append("search", params.search);
    if (params.verificationStatus) q.append("verificationStatus", params.verificationStatus);
    if (params.component) q.append("component", params.component);
    if (params.section) q.append("section", params.section);
    if (params.deprecated !== undefined) q.append("deprecated", String(params.deprecated));
    if (params.excludeTemplate) q.append("excludeTemplate", params.excludeTemplate);
    if (params.locale?.trim() && params.localePresence) {
      q.append("locale", params.locale.trim());
      q.append("localePresence", params.localePresence);
    }
    const qs = q.toString();
    return rc.cmsApiClient.get<StringRegistryItem[]>(`${STRINGS_LIST}${qs ? `?${qs}` : ""}`) as Promise<
      CMSApiResponse<StringRegistryItem[]> & { meta?: StringRegistryListMeta }
    >;
  },

  getString: async (
    rc: RunContextApp,
    documentId: string
  ): Promise<CMSApiResponse<{ data: StringRegistryItem }>> => {
    return rc.cmsApiClient.get<{ data: StringRegistryItem }>(
      `${STRING_DETAIL_V1(documentId)}?expand=full`
    );
  },

  getStringWithTemplate: async (
    rc: RunContextApp,
    documentId: string,
    templateName = "GLOBAL"
  ): Promise<CMSApiResponse<{ data: StringRegistryItem }>> => {
    return rc.cmsApiClient.get<{ data: StringRegistryItem }>(
      `${STRING_WITH_TEMPLATE(documentId)}?templateName=${encodeURIComponent(templateName)}`
    );
  },

  saveStringWithTemplate: async (
    rc: RunContextApp,
    documentId: string,
    templateItems: Array<{
      localeOverrides: Array<{ locale: string; value: string }>;
    }>,
    templateName = "GLOBAL"
  ): Promise<CMSApiResponse<{ data: StringRegistryItem }>> => {
    return rc.cmsApiClient.put<{ data: StringRegistryItem }>(STRING_WITH_TEMPLATE(documentId), {
      templateName,
      templateItems
    });
  },

  createString: async (rc: RunContextApp, data: CreateStringEntryPayload) => {
    return rc.cmsApiClient.post<{ data: { documentId: string } }>(ENTRIES_CRUD, {
      data: entryPayload(data)
    });
  },

  updateString: async (
    rc: RunContextApp,
    documentId: string,
    data: Partial<CreateStringEntryPayload>
  ) => {
    const lookupDocumentIds = data.lookupDocumentIds;
    const payload: Record<string, unknown> = {
      ...(data.stringResourceId !== undefined ? { stringResourceId: data.stringResourceId } : {}),
      ...(data.displayName !== undefined ? { displayName: data.displayName } : {}),
      ...(data.documentation !== undefined ? { documentation: data.documentation } : {}),
      ...(data.deprecated !== undefined ? { deprecated: data.deprecated } : {})
    };
    if (lookupDocumentIds) {
      const connect = lookupDocumentIds.map((id) => ({ documentId: id }));
      (payload as any).lookups = { set: connect };
    }
    return rc.cmsApiClient.put<{ data: { documentId: string } }>(ENTRY_UPDATE(documentId), {
      data: payload
    });
  },

  getPublishedLocales: async (rc: RunContextApp) => {
    return rc.cmsApiClient.get<{ code: string; name: string }[]>(LOCALES);
  },

  getTemplates: async (rc: RunContextApp) => {
    return rc.cmsApiClient.get<{ data: StringTemplate[] }>(TEMPLATES_LIST);
  },

  /**
   * Get single template, adapted to the legacy StringTemplate shape
   * (items[] built from the template's value rows).
   */
  getTemplate: async (rc: RunContextApp, documentId: string) => {
    const response = await rc.cmsApiClient.get<any>(TEMPLATE_VALUES(documentId));
    if ((response as any)?.error) {
      return response as CMSApiResponse<{ data: StringTemplate }>;
    }
    // CMSApiClient returns the body verbatim; /values payload is at the top
    // level (no { data } envelope)
    const raw: any = (response as any)?.templateId ? response : (response as any)?.data;
    if (!raw?.templateId) {
      return { error: { message: 'Template not found' } } as unknown as CMSApiResponse<{ data: StringTemplate }>;
    }
    const template: StringTemplate = {
      documentId: raw.templateId,
      name: raw.templateName,
      description: raw.description,
      module: raw.module,
      valueCount: raw.stringCount,
      items: (raw.strings || []).map((s: any) => ({
        stringRegistryEntry: {
          documentId: s.documentId,
          stringResourceId: s.stringResourceId,
          displayName: s.displayName,
          lookups: s.lookups,
          // GLOBAL values shipped by the values endpoint — lets the grid show
          // effective locales without the by-name enrichment round-trip
          ...(Array.isArray(s.globalLocaleOverrides) && s.globalLocaleOverrides.length
            ? { templateItems: [{ localeOverrides: s.globalLocaleOverrides }] }
            : {})
        } as unknown as StringRegistryItem,
        localeOverrides: s.localeOverrides || []
      }))
    };
    return { ...response, data: { data: template } } as CMSApiResponse<{ data: StringTemplate }>;
  },

  /**
   * Paginated template strings with server-side search/filters — one fast
   * page per request. Returns the raw payload:
   * { templateId, templateName, description, module, stringCount, memberCount,
   *   strings[], facets: { components, sections, overrideLocales },
   *   memberEntryIds, pagination }
   */
  getTemplateValuesPage: async (
    rc: RunContextApp,
    documentId: string,
    params: {
      page?: number;
      pageSize?: number;
      search?: string;
      component?: string;
      section?: string;
      overrideLocale?: string;
    } = {}
  ): Promise<{ error?: { message?: string }; payload?: any }> => {
    const q = new URLSearchParams();
    if (params.page) q.append("page", String(params.page));
    if (params.pageSize) q.append("pageSize", String(params.pageSize));
    if (params.search?.trim()) q.append("search", params.search.trim());
    if (params.component) q.append("component", params.component);
    if (params.section) q.append("section", params.section);
    if (params.overrideLocale) q.append("overrideLocale", params.overrideLocale);
    const qs = q.toString();
    const response = await rc.cmsApiClient.get<any>(`${TEMPLATE_VALUES(documentId)}${qs ? `?${qs}` : ""}`);
    if ((response as any)?.error) {
      return { error: (response as any).error };
    }
    const raw: any = (response as any)?.templateId ? response : (response as any)?.data;
    if (!raw?.templateId) {
      return { error: { message: "Template not found" } };
    }
    return { payload: raw };
  },

  /**
   * Upsert string values — replace semantics per entry, touches only the
   * listed entries
   */
  upsertTemplateValues: async (
    rc: RunContextApp,
    templateId: string,
    values: Array<{ entryDocumentId: string; localeOverrides: { locale: string; value: string }[] }>
  ) => {
    return rc.cmsApiClient.put(TEMPLATE_VALUES(templateId), { values });
  },

  /**
   * Remove one string entry (all its locales) from a template
   */
  removeTemplateValue: async (rc: RunContextApp, templateId: string, entryDocumentId: string) => {
    return rc.cmsApiClient.delete(TEMPLATE_VALUE_DELETE(templateId, entryDocumentId));
  },

  /**
   * Strapi GET often omits nested GLOBAL template rows on entries inside template `items`.
   * Fills them with ONE call to the GLOBAL by-name API (previously one with-template
   * call per string — an N+1 that doesn't scale to large templates).
   */
  enrichTemplateLocaleValues: async (rc: RunContextApp, template: StringTemplate): Promise<StringTemplate> => {
    const items = template.items || [];
    const needsEnrichment = items.some(
      (item) => item.stringRegistryEntry?.documentId && !hasGlobalLocaleData(item.stringRegistryEntry)
    );
    if (!needsEnrichment) return template;

    const res = await rc.cmsApiClient.get<{
      fromTemplate?: { strings?: { documentId: string; localeOverrides?: { locale: string; value: string }[] }[] };
    }>("/string-registry-templates/by-name/GLOBAL/strings");
    if (res.error) return template;
    const raw = res as any;
    const globalStrings =
      raw?.fromTemplate?.strings ?? raw?.data?.fromTemplate?.strings ?? [];
    const globalByEntryId = new Map<string, { locale: string; value: string }[]>(
      globalStrings.map((s: any) => [s.documentId, s.localeOverrides || []])
    );

    const merged = items.map((item) => {
      const entry = item.stringRegistryEntry;
      const eid = entry?.documentId;
      if (!eid || hasGlobalLocaleData(entry)) return item;
      const globalOverrides = globalByEntryId.get(eid);
      if (!globalOverrides?.length) return item;
      return {
        ...item,
        stringRegistryEntry: {
          ...entry,
          templateItems: [{ localeOverrides: globalOverrides }]
        } as StringRegistryItem
      };
    });
    return { ...template, items: merged };
  },

  /** Update template header (name/description/module) */
  updateTemplate: async (
    rc: RunContextApp,
    documentId: string,
    data: { name?: string; description?: string | null; module?: string | null }
  ) => {
    return rc.cmsApiClient.put(TEMPLATE_DETAIL(documentId), { data });
  },

  /**
   * Clone a template (header + every entry's locale overrides).
   * One row per entry, so large templates run past the 30s default —
   * see ConfigRegistryApis.cloneTemplate.
   */
  cloneTemplate: async (
    rc: RunContextApp,
    documentId: string,
    name?: string,
    description?: string
  ) => {
    return rc.cmsApiClient.post(
      TEMPLATE_CLONE(documentId),
      { name, description },
      true,
      API_TIMEOUT.LONG_OPERATION
    );
  },

  submitStringEntry: async (rc: RunContextApp, documentId: string) => {
    return rc.cmsApiClient.post<{ data: StringRegistryItem }>(WORKFLOW_SUBMIT(documentId), {});
  },

  approveStringEntry: async (rc: RunContextApp, documentId: string) => {
    return rc.cmsApiClient.post<{ data: StringRegistryItem }>(WORKFLOW_APPROVE(documentId), {});
  },

  rejectStringEntry: async (rc: RunContextApp, documentId: string) => {
    return rc.cmsApiClient.post<{ data: StringRegistryItem }>(WORKFLOW_REJECT(documentId), {});
  },

  reopenStringEntry: async (rc: RunContextApp, documentId: string) => {
    return rc.cmsApiClient.post<{ data: StringRegistryItem }>(WORKFLOW_REOPEN(documentId), {});
  }
};
